import { angleOf, dist, pt, type Pt } from '../model/geom';
import {
  mirrorEntity,
  rotateEntity,
  scaleEntity,
  translateEntity,
  type CadEntity,
} from '../model/entity';
import type { CadOp } from '../ops/operation';
import { extendEntity, offsetEntity, trimEntity } from '../ops/modifyGeom';
import { formatLength, fromDisplay } from '../model/units';
import type { Command, CmdContext, CommandRunner } from './types';

/**
 * 편집 명령 (작업지시서 P2-10 / P2-11)
 *   ERASE · MOVE · COPY · ROTATE · MIRROR · SCALE · OFFSET · TRIM · EXTEND
 *
 * 선택 프롬프트는 `useExisting: true` 를 쓴다 —
 * AutoCAD 처럼 "객체를 먼저 고르고 명령을 치는" 순서(명사→동사)도 지원하기 위해서다.
 */

/** 선택된 id 를 실제 엔티티로. 잠긴 레이어는 제외한다. */
function pick(ctx: CmdContext, ids: string[]): CadEntity[] {
  const out: CadEntity[] = [];
  for (const id of ids) {
    const e = ctx.scene.get(id);
    if (e && !ctx.scene.isLocked(e)) out.push(e);
  }
  return out;
}

/* ----------------------------------------------------------------- ERASE */

export const ERASE: Command = {
  name: 'ERASE',
  aliases: ['E', 'DEL'],
  label: '지우기',
  hint: '선택한 것을 지웁니다',
  *run(ctx: CmdContext): CommandRunner {
    const sel = yield { type: 'selection', message: '지울 객체 선택', useExisting: true };
    if (sel.t !== 'selection') return;

    const targets = pick(ctx, sel.ids);
    if (targets.length === 0) return;

    return [{ kind: 'delete', command: 'ERASE', entities: targets }];
  },
};

/* ------------------------------------------------------------------ MOVE */

export const MOVE: Command = {
  name: 'MOVE',
  aliases: ['M'],
  label: '이동',
  hint: '선택한 것을 옮깁니다',
  *run(ctx: CmdContext): CommandRunner {
    const sel = yield { type: 'selection', message: '이동할 객체 선택', useExisting: true };
    if (sel.t !== 'selection') return;
    const targets = pick(ctx, sel.ids);
    if (targets.length === 0) return;

    const from = yield { type: 'point', message: '기준점 지정' };
    if (from.t !== 'point') return;

    const to = yield {
      type: 'point',
      message: '두 번째 점 지정',
      base: from.p,
      preview: (cursor) =>
        targets.map((e) => ({
          ...translateEntity(e, cursor.x - from.p.x, cursor.y - from.p.y),
          id: `preview-${e.id}`,
        })),
    };
    if (to.t !== 'point') return;

    const dx = to.p.x - from.p.x;
    const dy = to.p.y - from.p.y;
    if (dx === 0 && dy === 0) return;

    return [
      {
        kind: 'update',
        command: 'MOVE',
        before: targets,
        after: targets.map((e) => translateEntity(e, dx, dy)),
      },
    ];
  },
};

/* ------------------------------------------------------------------ COPY */

export const COPY: Command = {
  name: 'COPY',
  aliases: ['CO', 'CP'],
  label: '복사',
  hint: '선택한 것을 복제합니다',
  *run(ctx: CmdContext): CommandRunner {
    const sel = yield { type: 'selection', message: '복사할 객체 선택', useExisting: true };
    if (sel.t !== 'selection') return;
    const targets = pick(ctx, sel.ids);
    if (targets.length === 0) return;

    const from = yield { type: 'point', message: '기준점 지정' };
    if (from.t !== 'point') return;

    const ops: CadOp[] = [];

    // AutoCAD 의 COPY 는 Enter 칠 때까지 반복 배치된다.
    // 매대를 줄줄이 놓는 작업에서 이 동작이 없으면 매우 답답하다.
    for (;;) {
      const to = yield {
        type: 'point',
        message: '두 번째 점 지정 (계속 복사, 종료 Enter)',
        base: from.p,
        allowEnter: true,
        preview: (cursor) =>
          targets.map((e) => ({
            ...translateEntity(e, cursor.x - from.p.x, cursor.y - from.p.y),
            id: `preview-${e.id}`,
          })),
      };

      if (to.t === 'enter' || to.t === 'esc') break;
      if (to.t !== 'point') continue;

      const dx = to.p.x - from.p.x;
      const dy = to.p.y - from.p.y;
      if (dx === 0 && dy === 0) continue;

      ops.push({
        kind: 'add',
        command: 'COPY',
        entities: targets.map((e) => ({ ...translateEntity(e, dx, dy), id: ctx.newId() })),
      });
    }

    return ops;
  },
};

/* ---------------------------------------------------------------- ROTATE */

export const ROTATE: Command = {
  name: 'ROTATE',
  aliases: ['RO'],
  label: '회전',
  hint: '선택한 것을 돌립니다',
  *run(ctx: CmdContext): CommandRunner {
    const sel = yield { type: 'selection', message: '회전할 객체 선택', useExisting: true };
    if (sel.t !== 'selection') return;
    const targets = pick(ctx, sel.ids);
    if (targets.length === 0) return;

    const c = yield { type: 'point', message: '회전 기준점 지정' };
    if (c.t !== 'point') return;

    const a = yield {
      type: 'point',
      message: '회전 각도 지정',
      base: c.p,
      preview: (cursor) => {
        const ang = angleOf(c.p, cursor);
        return targets.map((e) => ({
          ...rotateEntity(e, c.p, ang),
          id: `preview-${e.id}`,
        }));
      },
    };

    let angle: number;
    if (a.t === 'angle') angle = a.rad;
    else if (a.t === 'point') angle = angleOf(c.p, a.p);
    else return;

    if (angle === 0) return;

    return [
      {
        kind: 'update',
        command: 'ROTATE',
        before: targets,
        after: targets.map((e) => rotateEntity(e, c.p, angle)),
      },
    ];
  },
};

/* ---------------------------------------------------------------- MIRROR */

export const MIRROR: Command = {
  name: 'MIRROR',
  aliases: ['MI'],
  label: '대칭',
  hint: '선택한 것을 좌우/상하로 뒤집어 복사합니다',
  *run(ctx: CmdContext): CommandRunner {
    const sel = yield { type: 'selection', message: '대칭 복사할 객체 선택', useExisting: true };
    if (sel.t !== 'selection') return;
    const targets = pick(ctx, sel.ids);
    if (targets.length === 0) return;

    const a = yield { type: 'point', message: '대칭축 첫 번째 점' };
    if (a.t !== 'point') return;

    const b = yield {
      type: 'point',
      message: '대칭축 두 번째 점',
      base: a.p,
      preview: (cursor) =>
        targets.map((e) => ({ ...mirrorEntity(e, a.p, cursor), id: `preview-${e.id}` })),
    };
    if (b.t !== 'point') return;
    if (a.p.x === b.p.x && a.p.y === b.p.y) return;

    const keep = yield {
      type: 'keyword',
      message: '원본을 지울까요?',
      keywords: ['No', 'Yes'],
      defaultValue: 'No',
    };
    const eraseSource = keep.t === 'keyword' && keep.k === 'Yes';

    const mirrored = targets.map((e) => ({ ...mirrorEntity(e, a.p, b.p), id: ctx.newId() }));
    const ops: CadOp[] = [{ kind: 'add', command: 'MIRROR', entities: mirrored }];
    if (eraseSource) ops.push({ kind: 'delete', command: 'MIRROR', entities: targets });
    return ops;
  },
};

/* ----------------------------------------------------------------- SCALE */

export const SCALE: Command = {
  name: 'SCALE',
  aliases: ['SC'],
  label: '축척',
  hint: '선택한 것의 크기를 바꿉니다',
  *run(ctx: CmdContext): CommandRunner {
    const sel = yield { type: 'selection', message: '크기를 바꿀 객체 선택', useExisting: true };
    if (sel.t !== 'selection') return;
    const targets = pick(ctx, sel.ids);
    if (targets.length === 0) return;

    const c = yield { type: 'point', message: '기준점 지정' };
    if (c.t !== 'point') return;

    // 배율은 길이가 아니라 비율이므로 distance 토큰을 그대로 쓰면 안 된다.
    // 사용자가 "2" 를 치면 mm 로 해석되어 2000µm 가 들어온다 → 다시 비율로 환산한다.
    const k = yield { type: 'text', message: '배율 입력 (예: 2 는 두 배)', defaultValue: '1' };
    if (k.t !== 'text') return;

    const factor = Number(k.s.trim());
    if (!Number.isFinite(factor) || factor <= 0) {
      ctx.log('배율은 0보다 큰 숫자여야 합니다.');
      return;
    }
    if (factor === 1) return;

    return [
      {
        kind: 'update',
        command: 'SCALE',
        before: targets,
        after: targets.map((e) => scaleEntity(e, c.p, factor)),
      },
    ];
  },
};

/* ------------------------------------------------------------- CALIBRATE */

/**
 * 축척 보정 — "아는 거리를 재서 그 값에 맞춘다" (AutoCAD SCALE 의 참조(Reference) 옵션과 같은 개념)
 *
 * ★ 왜 필요한가
 *   PDF 를 가져올 때는 인쇄 축척(1:N)을 알아야 실제 치수가 나온다. 그런데
 *   오토캐드에서 "용지에 맞춤(Fit to paper)" 으로 출력한 PDF 는 1:153.5 같은
 *   **비표준 배율**이 되어 버려서, 목록에서 고를 수가 없다. 실제로 겪은 사례:
 *   1800mm 여야 할 곳이 1172.6mm 로 들어왔고(비율 약 1.535), 도면 전체가
 *   똑같은 비율로 어긋나 있었다.
 *
 *   이럴 때 배율을 역산해서 입력하라고 하는 것은 실무자에게 떠넘기는 것이다.
 *   대신 **아는 치수 한 군데를 재게 하고** 나머지는 여기서 계산한다.
 */
export const CALIBRATE: Command = {
  name: 'CALIBRATE',
  aliases: ['CAL'],
  label: '축척 보정',
  hint: '아는 거리를 재서 도면 전체 크기를 실제 치수에 맞춥니다',
  *run(ctx: CmdContext): CommandRunner {
    const unit = ctx.scene.doc.displayUnit;

    const sel = yield {
      type: 'selection',
      message: '보정할 객체 선택 (보통 전체 선택 Ctrl+A)',
      useExisting: true,
    };
    if (sel.t !== 'selection') return;
    const targets = pick(ctx, sel.ids);
    if (targets.length === 0) return;

    const a = yield { type: 'point', message: '실제 치수를 아는 구간의 첫 번째 점' };
    if (a.t !== 'point') return;

    const b = yield {
      type: 'point',
      message: '두 번째 점',
      base: a.p,
      preview: (cursor) => [
        { id: 'preview', layer: 'A-DIM', type: 'line', a: a.p, b: cursor } as CadEntity,
      ],
    };
    if (b.t !== 'point') return;

    const measured = dist(a.p, b.p);
    if (measured <= 0) {
      ctx.log('같은 점을 두 번 찍었습니다. 서로 다른 두 점을 지정하세요.');
      return;
    }

    ctx.log(`지금 이 구간은 ${formatLength(Math.round(measured), unit)} ${unit} 입니다.`);

    const want = yield {
      type: 'text',
      message: `이 구간의 실제 치수 (${unit})`,
      defaultValue: '',
    };
    if (want.t !== 'text') return;

    const actualValue = Number(want.s.trim().replace(/,/g, ''));
    if (!Number.isFinite(actualValue) || actualValue <= 0) {
      ctx.log('실제 치수는 0보다 큰 숫자여야 합니다.');
      return;
    }

    const factor = fromDisplay(actualValue, unit) / measured;
    if (!Number.isFinite(factor) || factor <= 0) {
      ctx.log('배율을 계산할 수 없습니다.');
      return;
    }
    if (Math.abs(factor - 1) < 1e-9) {
      ctx.log('이미 맞습니다 — 바꿀 것이 없습니다.');
      return;
    }

    ctx.log(
      `축척 보정: ×${factor.toFixed(4)} (객체 ${targets.length}개). ` +
        '틀렸으면 Ctrl+Z 로 되돌릴 수 있습니다.'
    );

    // 찍은 첫 점을 기준으로 키운다 — 사용자가 방금 본 그 지점이 제자리에 남아야
    // "어디가 어떻게 변했는지" 를 눈으로 확인할 수 있다.
    return [
      {
        kind: 'update',
        command: 'CALIBRATE',
        before: targets,
        after: targets.map((e) => scaleEntity(e, a.p, factor)),
      },
    ];
  },
};

/* ---------------------------------------------------------------- OFFSET */

export const OFFSET: Command = {
  name: 'OFFSET',
  aliases: ['O'],
  label: '간격 띄우기',
  hint: '일정 간격 띄워 평행 복사합니다',
  *run(ctx: CmdContext): CommandRunner {
    const d = yield { type: 'distance', message: '간격 지정' };
    if (d.t !== 'distance' || d.v <= 0) return;
    const distance = d.v;

    const ops: CadOp[] = [];

    // OFFSET 도 Enter 칠 때까지 반복한다 (통로 여러 개를 연달아 띄우는 작업)
    for (;;) {
      const sel = yield { type: 'selection', message: '간격 띄울 객체 선택 (종료 Enter)' };
      if (sel.t !== 'selection' || sel.ids.length === 0) break;

      const src = ctx.scene.get(sel.ids[0]!);
      if (!src) continue;

      const sidePt = yield {
        type: 'point',
        message: '띄울 방향 클릭',
        preview: (cursor) => {
          const r = offsetEntity(src, distance, cursor);
          return r ? [{ ...r, id: 'preview' }] : [];
        },
      };
      if (sidePt.t !== 'point') break;

      const result = offsetEntity(src, distance, sidePt.p);
      if (!result) {
        ctx.log('이 방향으로는 간격을 띄울 수 없습니다.');
        continue;
      }
      ops.push({
        kind: 'add',
        command: 'OFFSET',
        entities: [{ ...result, id: ctx.newId() }],
      });
    }

    return ops;
  },
};

/* ------------------------------------------------------------------ TRIM */

export const TRIM: Command = {
  name: 'TRIM',
  aliases: ['TR'],
  label: '자르기',
  hint: '교차선을 기준으로 필요 없는 부분을 잘라냅니다',
  *run(ctx: CmdContext): CommandRunner {
    // 절단 모서리를 지정하지 않고 Enter 를 치면 "화면의 모든 객체"가 절단선이 된다.
    // AutoCAD 의 기본 동작이며 실무에서 거의 항상 이 방식을 쓴다.
    const cut = yield {
      type: 'selection',
      message: '절단 모서리 선택 (전체 사용은 Enter)',
    };

    const cutters: CadEntity[] =
      cut.t === 'selection' && cut.ids.length > 0
        ? pick(ctx, cut.ids)
        : ctx.scene.entities.filter((e) => ctx.scene.isVisible(e));

    const ops: CadOp[] = [];

    for (;;) {
      const click = yield {
        type: 'point',
        message: '자를 부분 클릭 (종료 Enter)',
        allowEnter: true,
      };
      if (click.t === 'enter' || click.t === 'esc') break;
      if (click.t !== 'point') continue;

      // 클릭 지점에서 가장 가까운 대상 찾기
      const target = nearestAt(ctx, click.p);
      if (!target) {
        ctx.log('자를 대상을 찾지 못했습니다.');
        continue;
      }

      const r = trimEntity(target, cutters, click.p, ctx.newId, ctx.scene.blocks);
      if (!r) {
        ctx.log('교차하는 절단선이 없어 자를 수 없습니다.');
        continue;
      }

      ops.push({ kind: 'delete', command: 'TRIM', entities: [r.removed] });
      if (r.added.length > 0) {
        ops.push({ kind: 'add', command: 'TRIM', entities: r.added });
      }
    }

    return ops;
  },
};

/* ---------------------------------------------------------------- EXTEND */

export const EXTEND: Command = {
  name: 'EXTEND',
  aliases: ['EX'],
  label: '늘리기',
  hint: '선을 만날 때까지 늘립니다',
  *run(ctx: CmdContext): CommandRunner {
    const bnd = yield {
      type: 'selection',
      message: '경계 모서리 선택 (전체 사용은 Enter)',
    };

    const boundaries: CadEntity[] =
      bnd.t === 'selection' && bnd.ids.length > 0
        ? pick(ctx, bnd.ids)
        : ctx.scene.entities.filter((e) => ctx.scene.isVisible(e));

    const ops: CadOp[] = [];

    for (;;) {
      const click = yield {
        type: 'point',
        message: '늘릴 객체의 늘어날 쪽 클릭 (종료 Enter)',
        allowEnter: true,
      };
      if (click.t === 'enter' || click.t === 'esc') break;
      if (click.t !== 'point') continue;

      const target = nearestAt(ctx, click.p);
      if (!target) {
        ctx.log('늘릴 대상을 찾지 못했습니다.');
        continue;
      }

      const result = extendEntity(target, boundaries, click.p, ctx.scene.blocks);
      if (!result) {
        ctx.log('그 방향에서 만나는 경계가 없습니다.');
        continue;
      }

      ops.push({
        kind: 'update',
        command: 'EXTEND',
        before: [target],
        after: [result],
      });
    }

    return ops;
  },
};

/* -------------------------------------------------------------- 보조 */

/** 클릭 지점에서 가장 가까운(그리고 충분히 가까운) 편집 가능한 엔티티 */
function nearestAt(ctx: CmdContext, p: Pt): CadEntity | null {
  // 조리개는 화면 배율을 모르는 이 계층에서 정할 수 없으므로
  // 넉넉한 월드 반경(200mm)으로 후보를 모으고 거리로 고른다.
  const r = 200_000;
  const near = ctx.scene.queryBox({ minX: p.x - r, minY: p.y - r, maxX: p.x + r, maxY: p.y + r });

  let best: CadEntity | null = null;
  let bestD = Infinity;
  for (const e of near) {
    if (!ctx.scene.isVisible(e) || ctx.scene.isLocked(e)) continue;
    const box = ctx.scene.boxOf(e.id);
    if (!box) continue;
    const cx = Math.max(box.minX, Math.min(p.x, box.maxX));
    const cy = Math.max(box.minY, Math.min(p.y, box.maxY));
    const d = dist(p, pt(cx, cy));
    if (d < bestD) {
      bestD = d;
      best = e;
    }
  }
  return best;
}

export const MODIFY_COMMANDS: Command[] = [
  ERASE,
  MOVE,
  COPY,
  ROTATE,
  MIRROR,
  SCALE,
  CALIBRATE,
  OFFSET,
  TRIM,
  EXTEND,
];
